import { Injectable, inject } from '@angular/core';
import { BaseParamsService } from '../base-params.service';
import { Observable, of } from 'rxjs';
import { MemberIdentifier, MemberInterface } from '../_models/member-interface';
import { catchError, first, map, share, tap } from 'rxjs/operators';
import { MemberIdKeeperService } from '../member-id-keeper.service';
import { COOKIES_CONSENT_KEY } from '../cookie-consent/cookie-consent.service';
import storageFallback from 'local-storage-fallback';
import { SessionApiService } from '@app/core/session/session-api.service';
import { PermanentStorageService } from '@app/core/permanent-storage.service';
import { SessionOidcApiService } from '@app/core/session/session-oidc-api.service';
import { SessionApiProvider } from '@app/core/session/session-api-provider';
import { ApiInjectSessionResponse } from '@app/core/session/interfaces';
import { SessionApiMigrationProviderService } from '@app/core/session/session-api-migration-provider.service';

@Injectable()
export class SessionService {
  private baseParamsService = inject(BaseParamsService);
  private apiService = inject(SessionApiService);
  private oidcApiService = inject(SessionOidcApiService);
  private memberIdKeeper = inject(MemberIdKeeperService);
  private apiProvider = inject<SessionApiProvider>(SessionApiMigrationProviderService);
  private storage = inject(PermanentStorageService);

  private isAuthenticatedCached: boolean | null = null;
  private isAuthenticated$: Observable<boolean> | null = null;

  public deprecatedSignIn(
    memberIdentifier: MemberIdentifier | undefined,
    password: string,
    otpSecret: string | null,
  ): Observable<unknown> {
    return this.apiService.deprecatedSignIn({
      identifier: memberIdentifier?.identifier || '',
      identifier_type: memberIdentifier?.identifier_type || '',
      password: password,
      password_client_secret: otpSecret,
    }).pipe(
      tap((token: string) => {
        this.baseParamsService.clearAutologinParams();
        this.saveAuthToken(token);
      }),
    );
  }

  public startSignInFlow({ redirectUrl }: {
    redirectUrl?: string;
  }): Observable<string> {
    if (redirectUrl) {
      this.storage.setLoginRedirectUrl(redirectUrl);
    }
    return this.oidcApiService.startOidcSignIn();
  }

  public signOut(): Observable<unknown> {
    this.memberIdKeeper.clearMemberId();
    return this.apiProvider.signOut()
      .pipe(
        tap(() => this.clearAuthToken()),
      );
  }

  public isBasicallyAuthenticated(): Observable<boolean> {
    if (this.isAuthenticatedCached != null) {
      return of(this.isAuthenticatedCached);
    }
    if (this.isAuthenticated$) {
      return this.isAuthenticated$;
    }

    return this.isAuthenticated$ = this.authenticationCheckObservable();
  }

  public basicAutologinInfo(): Observable<Partial<MemberInterface> | null> {
    return this.apiService.getBasicAutologinInfo()
      .pipe(
        map((json) => {
          json.consents = { [COOKIES_CONSENT_KEY]: json[COOKIES_CONSENT_KEY] };
          return json;
        }),
        catchError(() => of(null)),
      );
  }

  public clearAuthToken(): void {
    storageFallback.removeItem(this.baseParamsService.authTokenKey());
    this.isAuthenticatedCached = false;
  }

  public saveAuthToken(authToken: string): void {
    storageFallback.setItem(this.baseParamsService.authTokenKey(), authToken);
    this.isAuthenticatedCached = true;
  }

  public saveOtpSecret(clientSecret: string): void {
    storageFallback.setItem(this.baseParamsService.otpClientSecretKey(), clientSecret);
  }

  public getOtpSecret(): string | null {
    return storageFallback.getItem(this.baseParamsService.otpClientSecretKey());
  }

  public createSessionFromMemberToken(token: string): Observable<boolean> {
    return this.apiService.injectSession(token).pipe(
      tap((response: ApiInjectSessionResponse) => this.saveAuthToken(response.auth_token)),
      map((response: ApiInjectSessionResponse) => response.status === 'ok'),
    );
  }

  public getGamesEmbedToken(): Observable<string> {
    return this.apiProvider.getGamesEmbedToken();
  }

  private authenticationCheckObservable(): Observable<boolean> {
    return this.apiProvider.authenticateBasically()
      .pipe(
        tap((isAuthenticated) => {
          this.isAuthenticatedCached = isAuthenticated;
          this.isAuthenticated$ = null;
        }),
        first(),
        share(),
      );
  }
}
